import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import InquiryDialog from "@/components/pricing/InquiryDialog";
import { MailLink } from "@/lib/contacts";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export function useCompany() {
  return useQuery({ queryKey: ["public-company"], queryFn: () => api.get("/public/company").then((r) => r.data), staleTime: 3600000 }).data;
}

export function useStartPlan() {
  const nav = useNavigate();
  const { session, user } = useAuth();
  return (plan, interval = "month", services = []) => {
    const qs = new URLSearchParams({ plan, interval, ...(services.length ? { services: services.join(",") } : {}) }).toString();
    if (session && user?.role === "owner") nav(`/app/checkout?${qs}`);
    else nav(`/signup?${qs}`);
  };
}

export function PublicFooter() {
  const [talk, setTalk] = useState(false);
  const co = useCompany();
  return (
    <footer className="bg-[#1C1917] text-stone-400" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <div className="font-display text-3xl text-white leading-tight">One location or twenty — start with the operating system your managers will actually use.</div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" data-testid="footer-cta-start" className="h-12 px-6 rounded-full bg-white text-stone-900 font-semibold inline-flex items-center justify-center gap-2">Start Free <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/pricing" data-testid="footer-cta-pricing" className="h-12 px-6 rounded-full border border-white/25 text-white font-semibold inline-flex items-center justify-center">See Pricing</Link>
          </div>
        </div>
        <div className="md:col-span-3 space-y-2 text-sm">
          <div className="text-white font-semibold mb-3">Product</div>
          <Link to="/what-is-86d" className="block hover:text-white" data-testid="footer-link-what-is">What is 86’d?</Link>
          <Link to="/features" className="block hover:text-white" data-testid="footer-link-features">Feature guides</Link>
          <Link to="/compare" className="block hover:text-white" data-testid="footer-link-compare">Compare</Link>
          <Link to="/resources" className="block hover:text-white" data-testid="footer-link-resources">Resources</Link>
          <Link to="/pricing" className="block hover:text-white" data-testid="footer-link-pricing">Pricing</Link>
          <Link to="/find-your-plan" className="block hover:text-white" data-testid="footer-link-finder">Find your plan</Link>
          <Link to="/training" className="block hover:text-white" data-testid="footer-link-training">Training &amp; Implementation</Link>
          <Link to="/profit-leak-calculator" className="block hover:text-white">Profit Leak Calculator</Link>
          <Link to="/security" className="block hover:text-white" data-testid="footer-link-security">Security &amp; Trust</Link>
        </div>
        <div className="md:col-span-3 space-y-2 text-sm">
          <div className="text-white font-semibold mb-3">Company</div>
          <button onClick={() => setTalk(true)} className="block hover:text-white" data-testid="footer-talk">Talk to 86'd</button>
          <Link to="/contact" className="block hover:text-white" data-testid="footer-link-contact">Contact</Link>
          <Link to="/support" className="block hover:text-white" data-testid="footer-link-support">Support</Link>
          <MailLink k="support" className="block hover:text-white break-all" />
          <Link to="/login" className="block hover:text-white">Log in</Link>
          <div className="text-white font-semibold pt-4 mb-3">Legal</div>
          <Link to="/privacy" className="block text-stone-300 hover:text-white underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded" data-testid="footer-link-privacy">Privacy Notice</Link>
          <Link to="/legal/terms" className="block text-stone-300 hover:text-white" data-testid="footer-link-terms">Terms of Service (pilot)</Link>
          <Link to="/legal/subprocessors" className="block text-stone-300 hover:text-white" data-testid="footer-link-subprocessors">Subprocessors</Link>
          <Link to="/legal/ai" className="block text-stone-300 hover:text-white" data-testid="footer-link-ai">AI processing</Link>
          <Link to="/legal/retention" className="block text-stone-300 hover:text-white" data-testid="footer-link-retention">Retention & deletion</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs space-y-1"><div>© {new Date().getFullYear()} 86'd — Restaurant Manager OS</div>{co?.attribution && <div className="text-stone-500" data-testid="footer-attribution">{co.attribution}</div>}</div>
      <InquiryDialog type="talk" open={talk} onOpenChange={setTalk} />
    </footer>
  );
}
